/**
 * RepConnect - External Provider Meeting Confirmation Page
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, X, Calendar, Clock, MapPin, User, Mail, Phone, 
  Download, AlertCircle, Shield, Star, Coffee
} from 'lucide-react';
import { ExternalProviderInvitation } from '../utils/externalProviderInvitation';
import { logger } from '../utils/logger';

const MeetingConfirmationView = () => {
  const [invitation, setInvitation] = useState(null);
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const actionParam = urlParams.get('action');
    
    if (!token || !actionParam || !['confirm', 'decline'].includes(actionParam)) {
      setError('Invalid or missing confirmation parameters');
      setLoading(false);
      return;
    }

    setAction(actionParam);

    // Get invitation details
    const invitationDetails = ExternalProviderInvitation.getInvitationByToken(token);
    
    if (!invitationDetails) {
      setError('Invitation not found or has expired');
      setLoading(false);
      return;
    }

    // Check if invitation is expired
    if (ExternalProviderInvitation.isInvitationExpired(invitationDetails)) {
      setError('This invitation has expired');
      setLoading(false);
      return;
    }

    // Check if invitation has already been responded to
    if (invitationDetails.status !== 'pending') {
      setError(`This invitation has already been ${invitationDetails.status}`);
      setLoading(false);
      return;
    }

    setInvitation(invitationDetails);
    setLoading(false);
  }, []);

  const handleConfirmation = async () => {
    if (!invitation) return;
    
    setProcessing(true);
    
    try {
      const response = ExternalProviderInvitation.handleProviderResponse(
        invitation.token, 
        action,
        {
          responseMethod: 'email_link',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      );
      
      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Failed to process response');
      }
    } catch (err) {
      logger.error('Error processing confirmation:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const downloadCalendarEvent = () => {
    if (!invitation) return;

    const formatDate = (dateStr) => {
      return dateStr.replace(/-/g, '');
    };

    const formatTime = (timeStr, dateStr) => {
      const [time, period] = timeStr.split(' ');
      let [hours, minutes] = time.split(':');
      
      if (period === 'PM' && hours !== '12') {
        hours = parseInt(hours) + 12;
      } else if (period === 'AM' && hours === '12') {
        hours = '00';
      }
      
      const date = new Date(dateStr);
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateTime = formatTime(invitation.meetingTime, invitation.meetingDate);
    const endDate = new Date(startDateTime.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'));
    endDate.setHours(endDate.getHours() + 1); // Assume 1-hour meeting
    const endDateTime = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RepConnect//EN',
      'BEGIN:VEVENT',
      `UID:${invitation.id}@meetingsyncpro.com`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:Lunch Meeting with ${invitation.salesRepName}`,
      `DESCRIPTION:Meeting with ${invitation.salesRepName} from ${invitation.salesRepCompany}\\n\\nLocation: ${invitation.restaurant}\\n\\nNotes: ${invitation.notes || 'No additional notes'}`,
      `LOCATION:${invitation.restaurant}`,
      `ORGANIZER;CN=${invitation.salesRepName}:MAILTO:${invitation.salesRepEmail}`,
      `ATTENDEE;CN=${invitation.providerName}:MAILTO:${invitation.providerEmail}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-${invitation.salesRepName.replace(/\s+/g, '-')}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Process Request</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact {invitation?.salesRepName || 'the sales representative'} directly.
          </p>
        </div>
      </div>
    );
  }

  if (result) {
    const isConfirmed = result.action === 'confirm';
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md max-w-2xl w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                isConfirmed ? 'bg-green-500' : 'bg-gray-500'
              }`}>
                {isConfirmed ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <X className="w-6 h-6" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold">
                  Meeting {isConfirmed ? 'Confirmed' : 'Declined'}
                </h1>
                <p className="text-blue-100">
                  Response recorded successfully
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {isConfirmed ? (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-gray-800 mb-2">
                    Thank you for confirming your meeting with <strong>{invitation.salesRepName}</strong>!
                  </p>
                  <p className="text-sm text-gray-600">
                    {invitation.salesRepName} has been notified of your confirmation and will reach out if any changes are needed.
                  </p>
                </div>

                {/* Meeting Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Meeting Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-blue-600" />
                      <span>
                        {new Date(invitation.meetingDate).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span>{invitation.meetingTime}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span>{invitation.restaurant}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span>{invitation.salesRepName}</span>
                    </div>
                  </div>
                  
                  {invitation.notes && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        <strong>Notes:</strong> {invitation.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={downloadCalendarEvent}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Add to Calendar</span>
                  </button>
                  <a
                    href={`mailto:${invitation.salesRepEmail}?subject=Meeting Confirmation - ${new Date(invitation.meetingDate).toLocaleDateString()}`}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email {invitation.salesRepName}</span>
                  </a>
                </div>

                {/* Contact Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Sales Representative Contact</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center space-x-2">
                      <User className="w-3 h-3 text-blue-600" />
                      <span>{invitation.salesRepName} - {invitation.salesRepCompany}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-3 h-3 text-blue-600" />
                      <a href={`mailto:${invitation.salesRepEmail}`} className="text-blue-600 hover:underline">
                        {invitation.salesRepEmail}
                      </a>
                    </div>
                    {invitation.salesRepPhone && invitation.salesRepPhone !== 'Contact via email' && (
                      <div className="flex items-center space-x-2">
                        <Phone className="w-3 h-3 text-blue-600" />
                        <span>{invitation.salesRepPhone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6 text-center">
                <div>
                  <p className="text-gray-800 mb-2">
                    You have declined the meeting with <strong>{invitation.salesRepName}</strong>.
                  </p>
                  <p className="text-sm text-gray-600">
                    {invitation.salesRepName} has been notified and may reach out to reschedule if appropriate.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Declined Meeting Details</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Date: {new Date(invitation.meetingDate).toLocaleDateString()}</div>
                    <div>Time: {invitation.meetingTime}</div>
                    <div>Location: {invitation.restaurant}</div>
                    <div>Representative: {invitation.salesRepName}</div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    If you'd like to reschedule or have questions, you can contact {invitation.salesRepName} directly at{' '}
                    <a href={`mailto:${invitation.salesRepEmail}`} className="font-medium underline">
                      {invitation.salesRepEmail}
                    </a>
                  </p>
                </div>
              </div>
            )}

            {/* RepConnect Promotion */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">
                      Simplify Future Representative Meetings
                    </h4>
                    <p className="text-sm text-gray-700 mb-3">
                      RepConnect is always <strong>free for healthcare providers</strong>. 
                      Set your preferences once and streamline all your pharmaceutical representative interactions.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-green-500" />
                        <span>Set availability preferences</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Coffee className="w-3 h-3 text-green-500" />
                        <span>Manage dining preferences</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3 text-green-500" />
                        <span>Track meeting history</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Shield className="w-3 h-3 text-green-500" />
                        <span>Automatic compliance tracking</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        Download Free App
                      </button>
                      <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                        Learn More
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial confirmation screen
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Meeting Invitation</h1>
              <p className="text-blue-100">
                {action === 'confirm' ? 'Confirm your meeting' : 'Decline this meeting'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Meeting Details */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Meeting Details</h2>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">
                    {invitation.salesRepName}
                  </p>
                  <p className="text-sm text-gray-600">{invitation.salesRepCompany}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">
                    {new Date(invitation.meetingDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="text-sm text-gray-600">{invitation.meetingTime}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">{invitation.restaurant}</p>
                  {invitation.restaurantAddress && (
                    <p className="text-sm text-gray-600">{invitation.restaurantAddress}</p>
                  )}
                </div>
              </div>
              
              {invitation.notes && (
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <strong>Additional Notes:</strong> {invitation.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Confirmation Message */}
          <div className={`mb-6 p-4 rounded-lg border ${
            action === 'confirm' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-start space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                action === 'confirm' 
                  ? 'bg-green-100' 
                  : 'bg-yellow-100'
              }`}>
                {action === 'confirm' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                )}
              </div>
              <div>
                <p className={`font-medium mb-1 ${
                  action === 'confirm' ? 'text-green-800' : 'text-yellow-800'
                }`}>
                  {action === 'confirm' ? 'Confirm this meeting?' : 'Decline this meeting?'}
                </p>
                <p className={`text-sm ${
                  action === 'confirm' ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {action === 'confirm' 
                    ? 'By confirming, you agree to attend this meeting at the specified date, time, and location. The sales representative will be notified immediately.'
                    : 'By declining, you will not attend this meeting. The sales representative will be notified and may reach out to reschedule.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleConfirmation}
              disabled={processing}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                action === 'confirm'
                  ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-green-400'
                  : 'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400'
              }`}
            >
              {processing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : action === 'confirm' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <X className="w-5 h-5" />
              )}
              <span>
                {processing 
                  ? 'Processing...' 
                  : action === 'confirm' 
                    ? 'Confirm Meeting' 
                    : 'Decline Meeting'
                }
              </span>
            </button>
            
            <a
              href={`mailto:${invitation.salesRepEmail}?subject=Meeting ${action === 'confirm' ? 'Confirmation' : 'Decline'} - ${new Date(invitation.meetingDate).toLocaleDateString()}`}
              className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <Mail className="w-5 h-5" />
              <span>Contact Representative</span>
            </a>
          </div>

          {/* Security Notice */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              This invitation was sent through RepConnect, a secure healthcare meeting coordination platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingConfirmationView;